/**
 * Session Warning Modal
 * Shows countdown when session is about to expire
 */

import { useEffect, useState } from "react";

interface SessionWarningModalProps {
  show: boolean;
  timeRemaining: number; // in milliseconds
  onExtend: () => void;
}

export function SessionWarningModal({
  show,
  timeRemaining,
  onExtend,
}: SessionWarningModalProps) {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    setSeconds(Math.floor(timeRemaining / 1000));
  }, [timeRemaining]);

  useEffect(() => {
    if (!show || seconds <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [show, seconds]);

  if (!show) {
    return null;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Session Expiring Soon
          </h3>
          <p className="text-gray-600">Your session will expire in:</p>
        </div>

        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4">
          <div className="text-center">
            <span className="text-4xl font-bold text-red-600">
              {minutes}:{remainingSeconds.toString().padStart(2, "0")}
            </span>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Click "Stay Logged In" to extend your session, or you will be
          automatically logged out.
        </p>

        <button
          onClick={onExtend}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
        >
          Stay Logged In
        </button>
      </div>
    </div>
  );
}
